/**
 * Onboarding Questionnaire API Service
 *
 * Handles API communication for user onboarding questionnaire
 * Integrates with backend Questionnaire Management APIs
 */

import { apiClient } from "./client";
import type { ApiError } from "./client";
import type {
  OnboardingQuestion,
  OnboardingQuestionType,
  OnboardingResponse,
  OnboardingSubmission,
  OnboardingStatus,
} from "../types/onboarding";

const QUESTIONNAIRE_BASE_URL = "/user-management/questionnaire";

interface QuestionnaireResponse {
  id: number;
  name: string;
  description: string;
  questionnaireType: string;
  questions: Array<{
    id: number;
    code: string;
    text: string;
    questionType: string;
    displayOrder: number;
    options: Array<{
      value: string;
      label: string;
      displayOrder: number;
    }>;
  }>;
}

interface AttemptResponse {
  id: number;
  questionnaireId: number;
  userId: number;
  startedAt: string;
  completedAt?: string;
}

interface BackendQuestion {
  id: number;
  code: string;
  text: string;
  section?: string;
  questionType: string;
  displayOrder: number;
  status?: number;
  minEntries?: number | null;
  maxEntries?: number | null;
  triggerOptionValues?: string[] | null;
  triggerBooleanValue?: boolean | null;
  commentFields?: Array<{
    id?: number;
    label: string;
    displayOrder: number;
  }> | null;
  options: Array<{
    id?: number;
    value: string;
    label: string;
    displayOrder: number;
    commentFields?: Array<{
      id?: number;
      label: string;
      displayOrder: number;
    }> | null;
  }> | null;
  subQuestions?: BackendQuestion[];
}

export const SUBSCRIPTION_REQUIRED_ERROR = "SUBSCRIPTION_REQUIRED";

class OnboardingApi {
  private currentAttemptId: number | null = null;
  private currentQuestionnaireId: number | null = null;

  /**
   * Get the default USER_ONBOARDING questionnaire
   * Uses: GET /api/user-management/questionnaire/default
   */
  private async getOrCreateQuestionnaire(): Promise<number> {
    const defaultResponse = await apiClient.get<QuestionnaireResponse>(
      `${QUESTIONNAIRE_BASE_URL}/default`,
    );
    return defaultResponse.data.id;
  }

  /**
   * Start onboarding by creating attempt and getting first question from response
   * Uses: POST /api/user-management/questionnaire/{questionnaireId}/attempt
   * Response: {id: attemptId, nextQuestion: BackendQuestion, answers: []}
   */
  async startOnboarding(): Promise<OnboardingResponse> {
    try {
      // Step 1: Get or create questionnaire
      this.currentQuestionnaireId = await this.getOrCreateQuestionnaire();

      // Step 2: Create an attempt - API returns attempt ID and first question
      const attemptResponse = await apiClient.post<{
        id: number;
        nextQuestion: BackendQuestion;
        answers?: Array<unknown>;
      }>(
        `${QUESTIONNAIRE_BASE_URL}/${this.currentQuestionnaireId}/attempt`,
        {},
      );

      // Capture the attempt ID
      this.currentAttemptId = attemptResponse.data.id;

      // Extract and transform the first question from nextQuestion field
      // If nextQuestion is null, it means the questionnaire is already completed
      if (!attemptResponse.data.nextQuestion) {
        this.reset();
        // Return null to indicate completion
        return null as unknown as OnboardingResponse; // Frontend should check if user already completed before calling startOnboarding
      }

      const firstQuestion = this.transformQuestion(
        attemptResponse.data.nextQuestion,
      );

      return {
        question: firstQuestion,
        hasMoreQuestions: true,
      };
    } catch (error) {
      if ((error as ApiError).statusCode === 403) {
        throw new Error(SUBSCRIPTION_REQUIRED_ERROR);
      }
      throw new Error("Failed to start onboarding questionnaire");
    }
  }

  /**
   * Submit an answer and get the next question from response
   * Uses: POST /api/user-management/questionnaire/{questionnaireId}/attempt/{attemptId}/answer
   * The answer response includes the next question directly in the data field
   */
  async submitAnswer(
    submission: OnboardingSubmission,
  ): Promise<OnboardingResponse | null> {
    if (!this.currentAttemptId) {
      throw new Error("No active attempt. Please start onboarding first.");
    }

    try {
      // Submit the answer - response contains next question directly in data field
      const response = await apiClient.post<BackendQuestion>(
        `${QUESTIONNAIRE_BASE_URL}/${this.currentQuestionnaireId}/attempt/${this.currentAttemptId}/answer`,
        {
          questionId: parseInt(submission.answer.questionId),
          singleChoiceValue: submission.answer.selectedOption || null,
          textValue: submission.answer.textValue || null,
          commentValues: submission.answer.commentValues ?? null,
        },
        { timeout: 600000 }, // 10 min — answer submission can be slow
      );

      // Check if there's a next question in the response data
      if (response.data && response.data.id) {
        const nextQuestion = this.transformQuestion(response.data);

        return {
          question: nextQuestion,
          hasMoreQuestions: true,
        };
      } else {
        // No more questions - onboarding complete
        this.reset();
        return null;
      }
    } catch (error) {
      throw new Error("Failed to submit answer");
    }
  }

  /**
   * Transform backend question format to frontend format
   */
  private transformQuestion(
    backendQuestion: BackendQuestion,
  ): OnboardingQuestion {
    if (!backendQuestion) {
      throw new Error("Invalid question data received from API");
    }

    const base: OnboardingQuestion = {
      id: backendQuestion.id.toString(),
      code: backendQuestion.code,
      questionText: backendQuestion.text,
      questionType: backendQuestion.questionType as OnboardingQuestionType,
      required: true,
      category: backendQuestion.section || undefined,
      commentFields:
        backendQuestion.commentFields?.map((cf) => ({
          id: cf.id,
          label: cf.label,
          displayOrder: cf.displayOrder,
        })) ?? null,
      options: (backendQuestion.options ?? []).map((opt, index: number) => ({
        id: opt.id?.toString() ?? `opt${index + 1}`,
        text: opt.label,
        value: opt.value,
        commentFields:
          opt.commentFields?.map((cf) => ({
            id: cf.id,
            label: cf.label,
            displayOrder: cf.displayOrder,
          })) ?? null,
      })),
      triggerOptionValues: backendQuestion.triggerOptionValues ?? null,
      triggerBooleanValue: backendQuestion.triggerBooleanValue ?? null,
    };

    if (
      backendQuestion.subQuestions &&
      backendQuestion.subQuestions.length > 0
    ) {
      base.subQuestions = backendQuestion.subQuestions
        .slice()
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((sq) => this.transformQuestion(sq));
    }

    return base;
  }

  /**
   * Get the next question without submitting an answer.
   * Used for LABEL_ONLY questions which don't have direct answers.
   * Uses: GET /api/user-management/questionnaire/{questionnaireId}/next-question/{currentQuestionId}
   */
  async getNextQuestionAfter(
    currentQuestionId: number,
  ): Promise<OnboardingResponse | null> {
    if (!this.currentQuestionnaireId) {
      throw new Error(
        "No active questionnaire. Please start onboarding first.",
      );
    }

    const response = await apiClient.get<BackendQuestion | null>(
      `${QUESTIONNAIRE_BASE_URL}/${this.currentQuestionnaireId}/next-question/${currentQuestionId}`,
    );

    const backendQuestion = response.data as BackendQuestion | null;

    if (!backendQuestion || !backendQuestion.id) {
      // No more questions – treat as completion
      this.reset();
      return null;
    }

    const nextQuestion = this.transformQuestion(backendQuestion);

    return {
      question: nextQuestion,
      hasMoreQuestions: true,
    };
  }

  /**
   * Check if user has already completed the questionnaire
   * Uses: GET /api/user-management/questionnaire/last_completed
   */
  async hasCompletedQuestionnaire(): Promise<boolean> {
    try {
      const response = await this.getLastCompletedQuestionnaire();
      return !!response && !!response.id;
    } catch {
      return false;
    }
  }

  /**
   * Get onboarding status for a user
   */
  async getOnboardingStatus(tenantId: number): Promise<OnboardingStatus> {
    try {
      // Get the questionnaire ID first
      const questionnaireId = await this.getOrCreateQuestionnaire();

      // Check if there's a completed attempt
      const response = await apiClient.get<AttemptResponse>(
        `${QUESTIONNAIRE_BASE_URL}/${questionnaireId}/attempt`,
        { timeout: 60000 }, // 60s — questionnaire attempt can be slow
      );

      const isComplete = !!response.data.completedAt;

      return {
        isComplete,
        completedAt: response.data.completedAt,
        answersCount: 0, // We don't track total questions count
        tenantId,
      };
    } catch {
      // If no attempt exists, onboarding is not complete
      return {
        isComplete: false,
        answersCount: 0,
        tenantId,
      };
    }
  }

  /**
   * Mark onboarding as complete
   */
  async completeOnboarding(_tenantId: number): Promise<void> {
    // Backend automatically marks the attempt as complete
    // when all questions are answered
  }

  /**
   * Reset onboarding state (for testing)
   */
  async resetOnboarding(_tenantId: number): Promise<void> {
    this.reset();
  }

  /**
   * Expose current questionnaire/attempt identifiers for auxiliary calls
   * (e.g. saving sub-question answers alongside the main onboarding flow).
   */
  getCurrentAttemptIds(): { questionnaireId: number; attemptId: number } {
    if (this.currentAttemptId == null || this.currentQuestionnaireId == null) {
      throw new Error("No active onboarding attempt");
    }
    return {
      questionnaireId: this.currentQuestionnaireId,
      attemptId: this.currentAttemptId,
    };
  }

  /**
   * Get the last completed questionnaire with answers
   * Uses: GET /api/user-management/questionnaire/last_completed
   * Response includes questions with selected: true/false in options
   */
  async getLastCompletedQuestionnaire(): Promise<{
    id: number;
    name: string;
    description: string;
    questionnaireType: string;
    version: number;
    questions: Array<{
      id: number;
      code: string;
      text: string;
      section?: string;
      questionType: string;
      displayOrder: number;
      status?: number;
      minEntries?: number | null;
      maxEntries?: number | null;
      /** Free-text answer for TEXT questions. */
      textValue?: string | null;
      textListValues?: string[] | null;
      commentFields?: Array<{
        id?: number;
        label: string;
        displayOrder: number;
      }> | null;
      commentValues?: Array<{
        questionCommentFieldId?: number | null;
        questionOptionCommentFieldId?: number | null;
        label: string;
        value: string;
      }> | null;
      triggerOptionValues?: string[] | null;
      triggerBooleanValue?: boolean | null;
      subQuestions?: any[] | null;
      options: Array<{
        id?: number;
        value: string;
        label: string;
        displayOrder: number;
        selected?: boolean;
      }>;
    }>;
  }> {
    const response = await apiClient.get<{
      id: number;
      name: string;
      description: string;
      questionnaireType: string;
      version: number;
      questions: Array<{
        id: number;
        code: string;
        text: string;
        section?: string;
        questionType: string;
        displayOrder: number;
        status?: number;
        minEntries?: number | null;
        maxEntries?: number | null;
        /** Free-text answer for TEXT questions. */
        textValue?: string | null;
        textListValues?: string[] | null;
        commentFields?: Array<{
          id?: number;
          label: string;
          displayOrder: number;
        }> | null;
        commentValues?: Array<{
          questionCommentFieldId?: number | null;
          questionOptionCommentFieldId?: number | null;
          label: string;
          value: string;
        }> | null;
        triggerOptionValues?: string[] | null;
        triggerBooleanValue?: boolean | null;
        subQuestions?: any[] | null;
        options: Array<{
          id?: number;
          value: string;
          label: string;
          displayOrder: number;
          selected?: boolean;
        }>;
      }>;
    }>(`${QUESTIONNAIRE_BASE_URL}/last_completed`);

    return response.data;
  }

  /**
   * Get the current attempt for a questionnaire
   * Uses: GET /api/user-management/questionnaire/{questionnaireId}/attempt
   * Returns the attempt ID needed for replacing answers.
   * First tries GET to retrieve an existing attempt;
   * if that fails (e.g. 404 not found), falls back to POST to create one.
   */
  async getAttempt(questionnaireId: number): Promise<AttemptResponse> {
    // Try GET first — retrieve existing attempt
    const response = await apiClient.get<AttemptResponse>(
      `${QUESTIONNAIRE_BASE_URL}/${questionnaireId}/attempt`,
      { timeout: 60000 },
    );

    if (response.success && response.data?.id) {
      return response.data;
    }

    // Fallback: POST to create/get attempt (get-or-create pattern)
    const createResponse = await apiClient.post<AttemptResponse>(
      `${QUESTIONNAIRE_BASE_URL}/${questionnaireId}/attempt`,
      {},
      { timeout: 60000 },
    );

    if (!createResponse.success || !createResponse.data) {
      throw new Error(
        createResponse.message || "Failed to fetch questionnaire attempt",
      );
    }

    return createResponse.data;
  }

  /**
   * Replace an answer for a question in a completed questionnaire
   * Uses: PUT /api/user-management/questionnaire/{questionnaireId}/attempt/{attemptId}/question/{questionId}/answer
   * Invalidates the current answer and creates a new one with history
   */
  async replaceAnswer(
    questionnaireId: number,
    attemptId: number,
    questionId: number,
    answerData: {
      textValue?: string;
      singleChoiceValue?: string;
      multiChoiceValues?: string[];
      booleanValue?: boolean;
      /** List of text entries for TEXT_LIST questions. */
      textListValues?: string[];
    },
  ): Promise<{
    id: number;
    question: { id: number };
    textValue: string | null;
    singleChoiceValue: string | null;
    multiChoiceValues: string[] | null;
    booleanValue: boolean | null;
    textListValues: string[] | null;
  }> {
    const response = await apiClient.put<{
      id: number;
      question: { id: number };
      textValue: string | null;
      singleChoiceValue: string | null;
      multiChoiceValues: string[] | null;
      booleanValue: boolean | null;
      textListValues: string[] | null;
    }>(
      `${QUESTIONNAIRE_BASE_URL}/${questionnaireId}/attempt/${attemptId}/question/${questionId}/answer`,
      answerData,
    );

    if (!response.success || !response.data) {
      throw new Error(response.message || "Failed to replace answer");
    }

    return response.data;
  }

  /**
   * Restore questionnaire/attempt identifiers (e.g. after reset, when navigating
   * back from the summary screen and re-submitting an already-answered question).
   */
  setCurrentIds(questionnaireId: number, attemptId: number): void {
    this.currentQuestionnaireId = questionnaireId;
    this.currentAttemptId = attemptId;
  }

  /**
   * Clear internal state
   */
  private reset(): void {
    this.currentAttemptId = null;
    this.currentQuestionnaireId = null;
  }
}

export const onboardingApi = new OnboardingApi();
export default onboardingApi;
